import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseInit';
import { useAuth } from '../../contexts/AuthContext';

/**
 * TeamSelector - Shared component for admin team selection
 * 
 * Provides a consistent dropdown for admins to switch between teams
 * Automatically loads teams from Firestore and handles selection
 * 
 * Usage:
 * const [selectedTeam, setSelectedTeam] = useState(userTeam);
 * <TeamSelector selectedTeam={selectedTeam} onTeamChange={setSelectedTeam} />
 */
export default function TeamSelector({ selectedTeam, onTeamChange, disabled = false }) {
  const { userRole, userTeam } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = userRole === 'admin';

  // Load teams on mount
  useEffect(() => {
    async function loadTeams() {
      if (!isAdmin) {
        setLoading(false);
        return;
      }

      try {
        const teamsRef = collection(db, 'artifacts/my-real-estate-assistant/teams');
        const snapshot = await getDocs(teamsRef);
        
        const teamsList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
          leadEmail: doc.data().leadEmail || 'No Lead'
        }));

        setTeams(teamsList);

        // Auto-select user's team if no selection
        if (!selectedTeam && userTeam && teamsList.length > 0) {
          const userTeamExists = teamsList.find(t => t.id === userTeam);
          if (userTeamExists) {
            onTeamChange?.(userTeam);
          } else if (teamsList.length > 0) {
            onTeamChange?.(teamsList[0].id);
          }
        }
      } catch (error) {
        console.error('❌ TeamSelector: Error loading teams:', error);
      } finally {
        setLoading(false);
      }
    }

    loadTeams();
  }, [isAdmin, userTeam, selectedTeam, onTeamChange]);

  // Don't render for non-admins
  if (!isAdmin) {
    return null;
  }

  // Don't render if no teams loaded yet
  if (loading || teams.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      <select
        id="team-selector"
        value={selectedTeam || ''}
        onChange={(e) => onTeamChange?.(e.target.value)}
        disabled={disabled || loading}
        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm truncate"
        aria-label="Select team to view"
      >
        {teams.map((team) => (
          <option key={team.id} value={team.id}>
            {team.name} ({team.leadEmail})
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * useTeamSelector - Custom hook for managing team selection state
 * 
 * Usage:
 * const { selectedTeam, setSelectedTeam, loading } = useTeamSelector();
 * <TeamSelector selectedTeam={selectedTeam} onTeamChange={setSelectedTeam} />
 */
export function useTeamSelector() {
  const { userTeam } = useAuth();
  const [selectedTeam, setSelectedTeam] = useState(userTeam);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userTeam && !selectedTeam) {
      setSelectedTeam(userTeam);
    }
    setLoading(false);
  }, [userTeam, selectedTeam]);

  return {
    selectedTeam,
    setSelectedTeam,
    loading
  };
}

